import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Calendar, Loader2, LogOut, LogIn } from 'lucide-react';
import { formatAtlanticDate, formatAtlanticTime } from '@/utils/time.utils.ts';
import { getStatusColor, getStatusLabel } from '@/utils/event-attendee-status.utils.ts';
import { MyEvent } from '@/api/myEventsService';
import { useTranslation } from 'react-i18next';

interface MyEventCardProps {
  event: MyEvent;
  variant: 'ongoing' | 'past';
  onCheckOut: (eventId: string, attendeeId: string) => void;
  isCheckingOut: boolean;
}

export const MyEventCard = ({ event, variant, onCheckOut, isCheckingOut }: MyEventCardProps) => {
  const { t, i18n } = useTranslation('common');

  const isOngoing = variant === 'ongoing';
  const cardClassName = isOngoing ? 'border-green-200 shadow-md' : 'transition-shadow hover:shadow-md';
  const headerClassName = isOngoing ? 'pb-3 bg-green-50/50' : 'pb-3';
  const contentClassName = isOngoing ? 'pt-4' : 'pt-0';

  return (
    <Card className={cardClassName}>
      <CardHeader className={headerClassName}>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-lg text-[var(--color-neutral-900)]">{event.eventName}</CardTitle>
            <CardDescription className="flex items-center gap-2 mt-1 text-[var(--color-neutral-700)]">
              <Calendar className="h-4 w-4" />
              <span title={t('events.startEvent')}>
                {formatAtlanticDate(event.eventDate, i18n.language)} {t('events.at')}{' '}
                {formatAtlanticTime(event.eventDate, i18n.language)}
              </span>
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className={contentClassName}>
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-[var(--color-neutral-900)]">
            {isOngoing ? t('pastEvents.checkedInMembers', 'Checked-in Members') : t('pastEvents.familyMembers')}
          </h4>
          <div className="grid grid-cols-1 gap-2">
            {event.attendees.map((attendee) => (
              <div
                key={attendee.attendeeId}
                className={`flex flex-col sm:flex-row sm:items-center justify-between rounded-lg p-3 shadow-sm gap-3 ${
                  isOngoing ? 'bg-white border border-green-100' : 'bg-neutral-300/20'
                }`}
              >
                <div className="flex flex-col gap-1 w-full sm:w-auto">
                  <div className="flex items-center gap-2">
                    <div
                      className={`h-2 w-2 rounded-full ${
                        attendee.registrationStatus === 'attended'
                          ? 'bg-green-500'
                          : attendee.registrationStatus === 'cancelled'
                            ? 'bg-red-500'
                            : 'bg-yellow-500'
                      } ${isOngoing ? 'animate-pulse' : ''}`}
                    ></div>
                    <span className="text-sm font-medium text-[var(--color-neutral-900)] break-words">
                      {attendee.attendeeName || t('labels.unknown')}
                    </span>
                  </div>
                  {(attendee.checkedInAt || attendee.checkedOutAt) && (
                    <div className="flex flex-col gap-1 ml-4 text-xs text-[var(--color-neutral-700)]">
                      {attendee.checkedInAt && (
                        <div className="flex items-center gap-2">
                          <LogIn className="h-3 w-3 text-[var(--color-neutral-500)]" />
                          <span>
                            {t('events.checkInResult.checkInTime', {
                              time: formatAtlanticTime(attendee.checkedInAt, i18n.language),
                            })}
                          </span>
                        </div>
                      )}
                      {attendee.checkedOutAt && (
                        <div className="flex items-center gap-2">
                          <LogOut className="h-3 w-3 text-[var(--color-neutral-500)]" />
                          <span>
                            {t('events.checkInResult.checkOutTime', {
                              time: formatAtlanticTime(attendee.checkedOutAt, i18n.language),
                            })}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {isOngoing && attendee.registrationStatus === 'attended' && !attendee.checkedOutAt ? (
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full sm:w-auto border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
                    onClick={() => onCheckOut(event.eventId, attendee.attendeeId)}
                    disabled={isCheckingOut}
                  >
                    {isCheckingOut ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <LogOut className="h-4 w-4 mr-1.5" />
                        {t('pastEvents.checkOut')}
                      </>
                    )}
                  </Button>
                ) : (
                  <div className="flex items-center gap-2 self-start sm:self-center">
                    <Badge variant={null} className={getStatusColor(attendee.registrationStatus)}>
                      {t(getStatusLabel(attendee.registrationStatus))}
                    </Badge>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
